import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { customerApi } from "@/lib/api";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];

export default function EditProfileDialog({ open, onOpenChange, profile, userId, onSaved }) {
  const [form, setForm] = useState({ fullName: "", dateOfBirth: "", gender: "", city: "", bloodGroup: "", allergies: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const b = profile?.basicInfo || {};
      const m = profile?.medicalInfo || {};
      setForm({
        fullName: b.fullName || "",
        dateOfBirth: b.dateOfBirth || "",
        gender: b.gender || "",
        city: b.city || "",
        bloodGroup: m.bloodGroup || "",
        allergies: Array.isArray(m.allergies) ? m.allergies.join(", ") : m.allergies || "",
      });
    }
  }, [open, profile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.fullName.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const res = await customerApi.updateProfile(userId, {
        basicInfo: {
          ...(profile?.basicInfo || {}),
          fullName: form.fullName.trim(),
          dateOfBirth: form.dateOfBirth,
          gender: form.gender,
          city: form.city.trim(),
        },
        medicalInfo: {
          ...(profile?.medicalInfo || {}),
          bloodGroup: form.bloodGroup,
          allergies: form.allergies ? form.allergies.split(",").map((s) => s.trim()).filter(Boolean) : [],
        },
      });
      toast.success("Profile updated");
      onSaved?.(res.data?.profile);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit profile</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input value={form.fullName} onChange={(e) => set("fullName", e.target.value)} className="mt-1.5 rounded-2xl h-11" data-testid="edit-name" />
          </div>
          <div>
            <Label>Date of birth (DD/MM/YYYY)</Label>
            <Input value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} placeholder="15/08/1990" className="mt-1.5 rounded-2xl h-11" data-testid="edit-dob" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="edit-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Blood group</Label>
              <Select value={form.bloodGroup} onValueChange={(v) => set("bloodGroup", v)}>
                <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="edit-blood"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {BLOOD_GROUPS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 rounded-2xl h-11" data-testid="edit-city" />
          </div>
          <div>
            <Label>Allergies (comma-separated)</Label>
            <Input value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="Penicillin, Peanuts" className="mt-1.5 rounded-2xl h-11" data-testid="edit-allergies" />
          </div>
        </div>
        <Button className="w-full rounded-2xl h-11 mt-2" onClick={save} disabled={saving} data-testid="save-profile">
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
